import { gql } from '@apollo/client'

import {
  ActivityFragment,
  CommentFragment,
  FormModelFragment,
  FormWithoutProjectFileFragment,
  ObservationFragment,
  ObservationWithoutProjectFileFragment,
  ProjectFileFragment,
  ProjectFolderFragment,
  ProjectFragment,
  ProjectRBACFragment,
  SubscriptionFragment,
  TaskFragmentWithoutProjectFileFragment,
} from '../fragments'

export const PROJECTS_AS_ADMIN = gql`
  query adminFindProjects {
    adminFindProjects {
      ...ProjectFragment
      subscription {
        ...SubscriptionFragment
      }
    }
  }
  ${ProjectFragment}
  ${SubscriptionFragment}
`

export const PROJECTS = gql`
  query projects($input: ProjectsFindInput!) {
    projects(input: $input) {
      ...ProjectFragment
    }

    sharedProjects {
      ...ProjectFragment
    }
  }
  ${ProjectFragment}
`

export const PROJECTS_WITH_SUBSCRIPTION = gql`
  query projectsWithSubscription($input: ProjectsFindInput!) {
    projects(input: $input) {
      ...ProjectFragment
      subscription {
        ...SubscriptionFragment
      }
    }
  }
  ${ProjectFragment}
  ${SubscriptionFragment}
`

export const PROJECT = gql`
  query project($id: ID!) {
    project(id: $id) {
      ...ProjectFragment
    }
  }
  ${ProjectFragment}
`

export const PROJECT_WITH_SUBSCRIPTION = gql`
  query projectWithSubscription($id: ID!) {
    project(id: $id) {
      ...ProjectFragment
      subscription {
        ...SubscriptionFragment
      }
    }
  }
  ${ProjectFragment}
  ${SubscriptionFragment}
`

export const PROJECT_PLANS = gql`
  query projectPlans($id: ID!) {
    project(id: $id) {
      ...ProjectFragment
      plans {
        ...ProjectFileFragment
      }
      planFolders {
        ...ProjectFolderFragment
      }
    }
  }
  ${ProjectFragment}
  ${ProjectFileFragment}
  ${ProjectFolderFragment}
`

export const PROJECT_DOCUMENTS = gql`
  query projectDocuments($id: ID!) {
    project(id: $id) {
      ...ProjectFragment
      documents {
        ...ProjectFileFragment
      }
      documentFolders {
        ...ProjectFolderFragment
      }
    }
  }
  ${ProjectFragment}
  ${ProjectFileFragment}
  ${ProjectFolderFragment}
`

export const PROJECT_FILE = gql`
  query projectFile($id: ID!) {
    projectFile(id: $id) {
      ...ProjectFileFragment
    }
  }
  ${ProjectFileFragment}
`

export const PROJECT_FILE_WITH_POSITIONS = gql`
  query projectFileWithPosition($id: ID!) {
    projectFile(id: $id) {
      ...ProjectFileFragment
      observations {
        ...ObservationWithoutProjectFileFragment
      }
      forms {
        ...FormWithoutProjectFileFragment
      }
      tasks {
        ...TaskFragmentWithoutProjectFileFragment
      }
    }
  }
  ${ProjectFileFragment}
  ${ObservationWithoutProjectFileFragment}
  ${FormWithoutProjectFileFragment}
  ${TaskFragmentWithoutProjectFileFragment}
`

export const PROJECT_FILE_WITH_COMMENTS_AND_ACTIVITIES = gql`
  query projectFileWithCommentsAndActivities($id: ID!) {
    projectFile(id: $id) {
      ...ProjectFileFragment
      activities {
        ...ActivityFragment
      }
      comments {
        ...CommentFragment
      }
    }
  }
  ${ProjectFileFragment}
  ${ActivityFragment}
  ${CommentFragment}
`

export const PROJECT_ALL_ACL = gql`
  query projectACL($id: ID!) {
    project(id: $id) {
      ...ProjectFragment
      allUserACL {
        ...ProjectRBACFragment
      }
    }
  }
  ${ProjectFragment}
  ${ProjectRBACFragment}
`

export const PROJECT_ACL = gql`
  query projectACL($id: ID!) {
    project(id: $id) {
      ...ProjectFragment
      userACL {
        ...ProjectRBACFragment
      }
    }
  }
  ${ProjectFragment}
  ${ProjectRBACFragment}
`

export const PROJECT_WITH_OBSERVATIONS = gql`
  query projectWithObservations($id: ID!) {
    project(id: $id) {
      ...ProjectFragment
      observations {
        ...ObservationFragment
        activities {
          ...ActivityFragment
        }
      }
    }
  }
  ${ProjectFragment}
  ${ObservationFragment}
  ${ActivityFragment}
`

export const PROJECT_WITH_FORM_MODELS = gql`
  query formModelList($organizationId: ID!) {
    projects(input: { organizationId: $organizationId }) {
      id
      name
      formModels {
        ...FormModelFragment
      }
    }
  }
  ${FormModelFragment}
`

export const PROJECT_FILE_VALIDATION_REQUEST_BY_TOKEN = gql`
  query projectFileValidationRequestByToken($token: String!) {
    projectFileValidationRequestByToken(token: $token) {
      projectName
      authorUsername
      fileName
      fileType
      fileUrl
      activities {
        ...ActivityFragment
      }
      comments {
        ...CommentFragment
        file {
          ...FileFragment
          downloadLink
        }
      }
    }
  }
  ${ActivityFragment}
  ${CommentFragment}
`
